import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { KalleHinweis } from './KalleHinweis';
import { useAuthStore } from '../stores/authStore';
import { useProgrammeStore } from '../stores/programmeStore';
import { useEnvironmentStatus } from './EnvironmentBanner';
import { useUsageTracking } from '../hooks/useUsageTracking';

/**
 * App-Rahmen: Sidebar plus Inhalt.
 *
 * **Ab Breite 1024 px (`lg`)** steht die Sidebar dauerhaft links, der Inhalt daneben —
 * so wie bisher.
 *
 * **Darunter** ist die Sidebar eine ausfahrbare Schublade und der Inhalt nutzt die
 * volle Breite. Vorher galt `ml-64` auf jeder Breite: auf einem iPhone 13 mini (375 px)
 * blieben dem Inhalt 111 px, also Querscrollen und abgeschnittene Karten. Das ließ sich
 * auch nicht im Browser umschalten — „Desktop-Website" ändert nur die gemeldete Breite,
 * nicht das Layout, und ein mobiles Layout gab es hier gar nicht.
 */
export function Layout() {
  // Einklapp-Zustand überlebt den Reload (nur Komfort, localStorage kann fehlen)
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('libero.sidebar.collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('libero.sidebar.collapsed', collapsed ? '1' : '0'); } catch { /* egal */ }
  }, [collapsed]);
  const [mobilOffen, setMobilOffen] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const { loadProgrammes } = useProgrammeStore();
  const { isVisible: bannerSichtbar } = useEnvironmentStatus();
  const ort = useLocation();
  useUsageTracking();

  useEffect(() => {
    if (isAuthenticated) {
      loadProgrammes();
    }
  }, [isAuthenticated, loadProgrammes]);

  // Nach jeder Navigation zu: sonst verdeckt die Schublade die Seite, die man gerade
  // aufgerufen hat.
  useEffect(() => { setMobilOffen(false); }, [ort.pathname]);

  // Solange die Schublade offen ist, nicht dahinter scrollen.
  useEffect(() => {
    if (!mobilOffen) return;
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = vorher; };
  }, [mobilOffen]);

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Mobile Kopfzeile mit Schublade-Knopf. Ab `lg` ausgeblendet. */}
      <div
        className="lg:hidden sticky z-20 flex items-center gap-3 h-14 px-3
                   bg-brand-black text-white"
        style={{ top: bannerSichtbar ? '1.5rem' : 0 }}
      >
        <button
          type="button"
          onClick={() => setMobilOffen(true)}
          aria-label="Menü öffnen"
          className="p-2 -ml-1 rounded-lg hover:bg-white/10 min-w-[44px] min-h-[44px]
                     flex items-center justify-center"
        >
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-semibold text-lg">Libero</span>
      </div>

      {/* Abdunkelung hinter der Schublade — Tippen schließt. */}
      {mobilOffen && (
        <button
          type="button"
          aria-label="Menü schließen"
          onClick={() => setMobilOffen(false)}
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
        />
      )}

      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        mobilOffen={mobilOffen}
        onSchliessen={() => setMobilOffen(false)}
      />

      <main
        className={`transition-all duration-200 ${collapsed ? 'lg:ml-16' : 'lg:ml-64'}`}
      >
        {/*
          Schmaler Rand auf dem Handy: 24 px links und rechts sind dort verschenkt.

          `overflow-x-auto` unter `lg` fängt zu breite Inhalte ein. Ohne das schiebt ein
          einziges zu breites Element — typisch eine Reiterleiste mit fünf, sechs Reitern
          oder eine Tabelle — das ganze Dokument über die Bildschirmbreite. iOS zoomt dann
          die komplette Seite heraus, damit sie passt: Schrift winzig, und die schwarze
          Kopfzeile endet mitten im Bild, weil sie nur 375 der ~600 px breit ist. Genau so
          sahen auftragsverfolgung und support-assistent am 20.08.2026 auf einem 13 mini
          aus.

          Mit dem Scroll-Container bleibt das Dokument 375 px breit, und der zu breite
          Inhalt wird innerhalb der Spalte gewischt. Die Höhe wächst weiter mit dem Inhalt
          (`overflow-y` bleibt damit faktisch ungenutzt), das Seiten-Scrollen bleibt also
          normal.

          Preis: ein Element, das sich am Fenster festkleben soll (`sticky top-0` direkt im
          Seitenfluss), klebt unter `lg` an diesem Container statt am Fenster. Sticky
          Tabellenköpfe sind davon nicht betroffen — die hängen in ihrem eigenen
          Scroll-Container. Ab `lg` ist alles unverändert.
        */}
        <div className="p-3 sm:p-4 lg:p-6 overflow-x-auto lg:overflow-visible">
          <KalleHinweis />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
